import handleError from '@fuse/core/errorHandler';
import { ChevronLeft, ChevronRight, Search } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetListOfStaffsMutation } from 'src/app/services/staffApi';
import { capitalizeFirstLetter, getAbsoluteFileUrl, renderFullName } from 'src/app/helpers/utils';
import { Button } from '@mui/material';
import { useSelector } from 'react-redux';
import { Avatar, List, ListItem, ListItemText, Typography } from '@mui/material';

const rows = 5;

const TemplateApproverSelector = ({ onChange, extraStyle }) => {
  const { t } = useTranslation('translations');
  const { i18n } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [getListOfStaffs] = useGetListOfStaffsMutation();
  const [notFoundDisplay, setNotFoundDisplay] = useState(true);
  const lang = i18n.language === 'ru' ? 'ru' : 'uzb_lat';
  const { isDarkMode } = useSelector((state) => state.themeSlice);

  useEffect(() => {
    if (!search.trim('')) {
      onChange({});
    }
  }, [search]);
  const getEmployeesList = () => {
    setEmployees([]);
    setIsLoading(true);
    getListOfStaffs({
      page,
      rows,
      name: search,
    })
      .unwrap()
      .then((response) => {
        setEmployees(response?.items);
        setTotalEmployees(response?.totalItems);
      })
      .catch(handleError)
      .finally(() => setIsLoading(false));
  };

  const handleEmplClick = (staff) => {
    onChange(staff);
    setSearch(staff.fullName.fio);
    setNotFoundDisplay(false);
  };

  const handleSearchChange = (event) => {
    if (page > 1) {
      setPage(1);
    }

    const employee = event.target.value.trim();
    setSearch(employee);

    if (!employee) setSearch('');
  };

  const handleClearSearch = () => {
    setPage(1);
    setSearch('');
  };

  const totalPages = useMemo(() => {
    return Math.ceil(totalEmployees / rows);
  }, [totalEmployees]);

  useEffect(() => {
    if (search?.length > 2) {
      setIsLoading(true);
      const delayDebounceFn = setTimeout(
        () => {
          getEmployeesList();
        },
        search.length ? 500 : 0
      );

      return () => clearTimeout(delayDebounceFn);
    } else {
      setEmployees([]);
      setTotalEmployees(0);
    }
  }, [search, page]);

  return (
    <div>
      <div className="relative flex items-center justify-center gap-3 mt-3 border-1 ">
        {search && (
          <span
            className="absolute right-0 z-40 block mr-12 text-xl font-medium text-white dark:bg-dark hover:cursor-pointer"
            onClick={handleClearSearch}
          >
            &times;
          </span>
        )}

        <input
          type="text"
          className={`w-full  h-32 p-2 border-1 rounded ${
            extraStyle
              ? isDarkMode
                ? 'bg-gray-700 text-white border-gray-600'
                : ''
              : 'bg-inherit py-10 px-9'
          }`}
          value={search}
          onChange={handleSearchChange}
          placeholder={t('Search')}
        />
      </div>

      {search && (
        <div>
          <List className="p-0 pb-0 mb-0">
            {search.length > 2 && employees?.length === 0 && !isLoading && notFoundDisplay && (
              <ListItem className="px-0 space-x-4">
                <ListItemText className="text-center">{t('NotFound')}</ListItemText>
              </ListItem>
            )}
            {search &&
              employees.map((empl) => (
                <ListItem
                  key={empl.id}
                  className="px-0 space-x-8 cursor-pointer hover:bg-grey-50 dark:hover:bg-lightblack border-b-1"
                  onClick={() => handleEmplClick(empl)}
                >
                  <Avatar src={getAbsoluteFileUrl(empl?.photo)} alt={empl.name} />
                  <ListItemText>
                    <div className="flex flex-col">
                      <Typography
                        className="text-base capitalize dark:text-white font-inter font-700"
                        color="primary.main"
                        paragraph={false}
                      >
                        {capitalizeFirstLetter(empl.fullName.name)}{' '}
                        {capitalizeFirstLetter(empl.fullName.surname)}
                      </Typography>
                      <Typography className="mt-2 text-sm text-gray-600 font-inter">
                        {empl.placeOfWork.positionName && empl?.placeOfWork?.positionName[lang]}
                      </Typography>
                    </div>
                  </ListItemText>
                </ListItem>
              ))}
          </List>
          {search && employees.length < totalEmployees && totalEmployees > rows && (
            <div className="flex items-center justify-center h-10 mt-10">
              <Button type="text" onClick={() => setPage(page - 1)} disabled={page < 2}>
                <ChevronLeft className="dark:text-white" />
              </Button>
              <span>
                {page} / {totalPages}
              </span>
              <Button type="text" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                <ChevronRight className="dark:text-white" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateApproverSelector;
